import { useMemo } from "react";
import type { useWeightMacroTrackerBlock } from "./useWeightMacroTrackerBlock";
import { CALORIE_UNIT, MACRO_UNIT, WATER_UNIT, WEIGHT_UNIT } from "../../../utils/nutritionUnitConversions";

type TrackerState = ReturnType<typeof useWeightMacroTrackerBlock>;

export const useLogEntryView = (state: TrackerState, editEntryId: string | undefined) => {
  const editingEntry = useMemo(() => {
    if (!editEntryId) return undefined;
    return (
      state.weightEntries.find((o) => o.id === editEntryId) ??
      state.foodEntries.find((o) => o.id === editEntryId) ??
      state.waterEntries.find((o) => o.id === editEntryId)
    );
  }, [editEntryId, state.weightEntries, state.foodEntries, state.waterEntries]);

  const editingType = editingEntry?.type;

  const updateWeight = async (valueInLb: number) => {
    if (!editingEntry) return;
    await state.updateEntry({ id: editingEntry.id, measurement: { value: valueInLb, unit: WEIGHT_UNIT } });
    state.goBackFromLogEntry();
  };

  const updateWater = async (valueInMl: number) => {
    if (!editingEntry) return;
    await state.updateEntry({ id: editingEntry.id, measurement: { value: valueInMl, unit: WATER_UNIT } });
    state.goBackFromLogEntry();
  };

  const updateFood = async (params: {
    totalCalories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  }) => {
    if (!editingEntry) return;
    await state.updateEntry({
      id: editingEntry.id,
      measurement: { value: params.totalCalories, unit: CALORIE_UNIT },
      components: [
        { code: { text: "Protein" }, valueQuantity: { value: params.protein, unit: MACRO_UNIT } },
        { code: { text: "Carbohydrate" }, valueQuantity: { value: params.carbs, unit: MACRO_UNIT } },
        { code: { text: "Fat" }, valueQuantity: { value: params.fat, unit: MACRO_UNIT } },
        { code: { text: "Fiber" }, valueQuantity: { value: params.fiber, unit: MACRO_UNIT } },
      ],
    });
    state.goBackFromLogEntry();
  };

  return { editingEntry, editingType, updateWeight, updateWater, updateFood };
};
